/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Flex, IconButton, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text, useToast } from '@chakra-ui/react';
import { faDownload, faSearchMinus, faSearchPlus, faExclamationTriangle, faSpellCheck, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { gql, useMutation } from '@apollo/client';
import mammoth from 'mammoth';
import JSZip from 'jszip';
import Lottie from 'lottie-react';
import { useTranslation } from 'react-i18next';
import AnimationFile from '../../assets/lotties/68784-transfer-cloud.json';
import AnimationFile1 from '../../assets/lotties/AI loading.json';
import useStore from '../../store';
import { isUserPermitted } from '../../utils/checker';
import { locateSuggestionRange, wrapRangeInSpan, SPELL_FLAG_ACTIVE_CLASS, SPELL_FLAG_CSS } from '../../utils/spell-highlight';

const CHECK_SPELLING = gql`
	mutation CheckSpelling($text: String!) {
		checkSpelling(text: $text) {
			suggestions {
				original
				suggestion
				context
			}
			notes {
				note
			}
		}
	}
`;

const APPLY_DOCX_CORRECTIONS = gql`
	mutation ApplyDocxCorrections($fileUrl: String!, $corrections: [DocxCorrectionInput!]!) {
		applyDocxCorrections(fileUrl: $fileUrl, corrections: $corrections) {
			fileName
			localPath
			base64
		}
	}
`;

const REJECT_SPELLING_SUGGESTION = gql`
	mutation RejectSpellingSuggestion($original: String!, $suggestion: String!) {
		rejectSpellingSuggestion(original: $original, suggestion: $suggestion)
	}
`;

const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// Decodes a base64 string (as returned by applyDocxCorrections) into a
// downloadable Blob without needing atob() to handle the whole payload at
// once — large files decode fine here since this is just byte conversion,
// no string concatenation blowup.
function base64ToBlob(base64, mimeType) {
	const byteChars = atob(base64);
	const byteNumbers = new Array(byteChars.length);
	for (let i = 0; i < byteChars.length; i++) {
		byteNumbers[i] = byteChars.charCodeAt(i);
	}
	return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
}

function triggerBlobDownload(blob, fileName) {
	const blobUrl = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = blobUrl;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(blobUrl);
}

const ZOOM_STEP = 0.1;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;

// Word default page: 8.5in x 11in @ 96dpi = 816 x 1056px, 1in margins = 96px
const PAGE_W = 816;
const PAGE_MARGIN = 96;

// ── mammoth style map ─────────────────────────────────────────────────────
// mammoth strips most inline formatting by default (bold/italic/underline
// survive, but font-size/color/alignment/table borders don't map to HTML
// automatically) — this style map keeps common Word constructs recognizable
// so the render stays close to the original .docx rather than looking like
// plain reflowed text.
const STYLE_MAP = [
	"p[style-name='Title'] => h1.docx-title:fresh",
	"p[style-name='Heading 1'] => h1:fresh",
	"p[style-name='Heading 2'] => h2:fresh",
	"p[style-name='Heading 3'] => h3:fresh",
	"p[style-name='Heading 4'] => h4:fresh",
	"p[style-name='Subtitle'] => p.docx-subtitle:fresh",
	"p[style-name='Quote'] => blockquote:fresh",
	"p[style-name='Intense Quote'] => blockquote.docx-intense-quote:fresh",
	"r[style-name='Strong'] => strong",
	"r[style-name='Emphasis'] => em",
];

// mammoth parses w:jc (alignment) and w:ind (hanging/first-line indent) but
// never emits them into HTML — they only affect list detection internally.
// The style map DSL can only match on style-name, not raw properties, so we
// re-encode alignment/indent as a synthetic paragraph styleName (mammoth's
// style-name matcher only fires when the paragraph doesn't already carry a
// real Word style, so a second style map (ALIGN_STYLE_MAP) can turn the
// synthetic name back into real CSS classes without colliding with the
// Heading/Title/Quote rules above.
function alignStyleName(paragraph) {
	if (paragraph.styleName) return null;
	const align = paragraph.alignment === 'both' ? 'justify' : paragraph.alignment;
	const hanging = paragraph.indent && paragraph.indent.hanging ? 'hang' : paragraph.indent && paragraph.indent.firstLine ? 'first' : null;
	const parts = [align, hanging].filter(Boolean);
	return parts.length ? `DocxAlign ${parts.join('-')}` : null;
}

function transformParagraph(paragraph) {
	const styleName = alignStyleName(paragraph);
	return styleName ? { ...paragraph, styleName } : paragraph;
}

const ALIGN_VALUES = ['center', 'right', 'justify'];
const INDENT_VALUES = ['hang', 'first'];
const ALIGN_STYLE_MAP = [];
ALIGN_VALUES.forEach((align) => {
	ALIGN_STYLE_MAP.push(`p[style-name='DocxAlign ${align}'] => p.docx-align-${align}:fresh`);
	INDENT_VALUES.forEach((indent) => {
		ALIGN_STYLE_MAP.push(`p[style-name='DocxAlign ${align}-${indent}'] => p.docx-align-${align}.docx-indent-${indent}:fresh`);
	});
});
INDENT_VALUES.forEach((indent) => {
	ALIGN_STYLE_MAP.push(`p[style-name='DocxAlign ${indent}'] => p.docx-indent-${indent}:fresh`);
});

// mammoth parses each run's w:rFonts/w:sz (the literal font + size Word used
// for that run) but never emits them — only bold/italic/underline survive to
// HTML. Khmer documents lean on direct per-run font switches (e.g. a Muol
// Light heading-style run inline in an otherwise Siemreap paragraph) rather
// than named character styles, so without this the whole document silently
// collapses onto one fallback font/size regardless of what Word actually
// shows. We slugify the run's actual font name + point size into a synthetic
// styleName and map every (font, size) pair we actually see back to an
// inline style via a style map built from the real document data — this
// runs once per document load, not against a hardcoded combination table.
const KNOWN_KHMER_FONTS = {
	'khmer os siemreap': "'KhmerOSSiemreap', 'Khmer OS Siemreap'",
	'khmer os muol light': "'KhmerOSMuolLight', 'Khmer OS Muol Light'",
	'khmer os muol': "'KhmerOSMuolLight', 'Khmer OS Muol Light'",
	'khmer os battambang': "'Hanuman'",
	'khmer os bokor': "'Bokor'",
	'khmer os': "'NotoSansKhmer'",
	tacteing: "'Tacteing'",
};

function fontSlug(font) {
	return font
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/(^-|-$)/g, '');
}

function runStyleName(run) {
	if (run.styleName || (!run.font && !run.fontSize)) return null;
	const parts = [];
	if (run.font) parts.push(`f-${fontSlug(run.font)}`);
	if (run.fontSize) parts.push(`s-${run.fontSize}`);
	return `DocxRun ${parts.join(' ')}`;
}

function transformRun(run) {
	const styleName = runStyleName(run);
	return styleName ? { ...run, styleName } : run;
}

// mammoth only reads the *Latin* run properties — w:rFonts/w:ascii for font
// and w:sz for size — never their complex-script counterparts w:cs and
// w:szCs. Word uses w:cs/w:szCs (not w:ascii/w:sz) to render Khmer glyphs,
// and .docx files routinely set them differently on the same run (e.g.
// w:ascii="Calibri" w:sz=16 for the Latin fallback metrics, w:cs="Khmer OS
// Muol Light" w:szCs=24 for what's actually shown) — so trusting run.font/
// run.fontSize alone silently renders Khmer text in the wrong font *and*
// the wrong size. We re-read word/document.xml directly to recover w:cs/
// w:szCs per <w:r>, then correlate with mammoth's parsed run list by text
// content (not raw position) — content in a text box or drawing gets
// relocated by mammoth to right after its anchor paragraph (readChildElements
// + insertExtra in mammoth's body-reader), so a flat document-order zip of
// the two lists silently misaligns everything from that point on even when
// the total counts happen to match. Matching each mammoth run to the next
// xml run with the same text is immune to that reordering: it just skips
// whatever doesn't line up instead of cascading a corruption.
async function readComplexScriptRunProps(arrayBuffer) {
	const zip = await JSZip.loadAsync(arrayBuffer);
	const xmlText = await zip.file('word/document.xml').async('text');
	const xml = new DOMParser().parseFromString(xmlText, 'application/xml');
	// DOMParser never throws on malformed XML — it returns a document whose
	// root is a <parsererror> element instead, so a bad parse would otherwise
	// silently produce zero runs rather than surface as a caught rejection.
	if (xml.getElementsByTagName('parsererror').length > 0) {
		throw new Error('Failed to parse word/document.xml as XML');
	}
	// mc:AlternateContent (used for e.g. legacy textbox/VML fallbacks) carries
	// the same content twice — once in mc:Choice (what Word 2010+ and mammoth
	// both render) and once in mc:Fallback (older-Word compatibility, never
	// rendered). A plain getElementsByTagName("w:r") walks both, double-
	// counting runs mammoth only sees once, so we drop mc:Fallback subtrees.
	Array.from(xml.getElementsByTagName('mc:Fallback')).forEach((fallback) => {
		fallback.parentNode.removeChild(fallback);
	});
	// getElementsByTagName("w:r") is recursive, so a run that anchors a
	// drawing (image/textbox) picks up its nested w:txbxContent's <w:t> too
	// when we read "text" naively — but that textbox's own <w:r> elements are
	// *also* independently present later in this same flat list (nested tags
	// still match a tag-name query at any depth), so counting the anchor
	// run's nested text against the anchor run itself would double it up.
	// mammoth treats the anchor run as text-less and relocates the textbox's
	// paragraphs to right after their enclosing paragraph instead of leaving
	// them nested — matching by content further down naturally lines each
	// textbox run up with its own independent <w:r> entry, so all this needs
	// to do is keep the anchor run's own text scoped to its *direct* text,
	// not text belonging to a textbox nested inside it.
	const runNodes = Array.from(xml.getElementsByTagName('w:r'));
	return runNodes.map((runNode) => {
		const textNodes = Array.from(runNode.getElementsByTagName('w:t')).filter((t) => {
			for (let n = t.parentNode; n && n !== runNode; n = n.parentNode) {
				if (n.tagName === 'w:txbxContent') return false;
			}
			return true;
		});
		const text = textNodes.map((n) => n.textContent).join('');
		const rFonts = runNode.getElementsByTagName('w:rFonts')[0];
		const szCs = runNode.getElementsByTagName('w:szCs')[0];
		const font = rFonts ? rFonts.getAttribute('w:cs') || rFonts.getAttribute('w:ascii') : null;
		const szCsVal = szCs && szCs.getAttribute('w:val');
		const fontSize = /^[0-9]+$/.test(szCsVal) ? parseInt(szCsVal, 10) / 2 : null;
		return { font, fontSize, text };
	});
}

function applyComplexScriptRunProps(document, csProps) {
	const runs = mammoth.transforms.getDescendantsOfType(document, 'run');
	const used = new Array(csProps.length).fill(false);
	let cursor = 0;
	let matched = 0;
	runs.forEach((run) => {
		const runText = mammoth.transforms
			.getDescendantsOfType(run, 'text')
			.map((t) => t.value)
			.join('');
		if (!runText) return;
		// Prefer the next unused match at or after the cursor (keeps ordinary
		// sequential runs, including repeated words, aligned in document
		// order) but fall back to searching the whole list — content mammoth
		// relocated (e.g. a text box's paragraphs, moved to right after their
		// anchor paragraph) can sit *before* the cursor by the time we reach
		// it, and a forward-only search would permanently miss it.
		let found = csProps.findIndex((p, idx) => idx >= cursor && !used[idx] && p.text === runText);
		if (found === -1) {
			found = csProps.findIndex((p, idx) => !used[idx] && p.text === runText);
		}
		if (found === -1) return;
		used[found] = true;
		if (csProps[found].font) run.font = csProps[found].font;
		if (csProps[found].fontSize) run.fontSize = csProps[found].fontSize;
		cursor = found + 1;
		matched++;
	});
	if (matched < runs.length * 0.8) {
		console.warn(`DocxViewer: only matched ${matched}/${runs.length} runs by text content when applying w:cs/w:szCs`);
	}
}

const EMU_PER_PX = 9525; // 914400 EMU/inch ÷ 96 px/inch

// mammoth's Image document node never captures wp:extent (the drawing's
// intended display size in EMUs) — every <img> it emits is unsized, so our
// CSS's max-width:100% stretches small inline art (e.g. a QR code) up to
// fill the page width instead of showing it at the size Word actually used.
// Unlike runs, drawings aren't relocated by mammoth's "extra content"
// mechanism (readDrawingElement doesn't call .toExtra()), so a straight
// document-order pairing between wp:extent values and generated <img> tags
// is safe — there's no reordering to guard against here.
async function readImageExtents(arrayBuffer) {
	const zip = await JSZip.loadAsync(arrayBuffer);
	const xmlText = await zip.file('word/document.xml').async('text');
	const xml = new DOMParser().parseFromString(xmlText, 'application/xml');
	if (xml.getElementsByTagName('parsererror').length > 0) {
		throw new Error('Failed to parse word/document.xml as XML');
	}
	Array.from(xml.getElementsByTagName('mc:Fallback')).forEach((fallback) => {
		fallback.parentNode.removeChild(fallback);
	});
	return Array.from(xml.getElementsByTagName('wp:extent')).map((extent) => ({
		width: Math.round(parseInt(extent.getAttribute('cx'), 10) / EMU_PER_PX),
		height: Math.round(parseInt(extent.getAttribute('cy'), 10) / EMU_PER_PX),
	}));
}

function applyImageExtents(html, extents) {
	if (!extents.length) return html;
	let i = 0;
	return html.replace(/<img\b[^>]*>/g, (tag) => {
		const extent = extents[i++];
		if (!extent || /\bwidth=/.test(tag)) return tag;
		return tag.replace(/^<img/, `<img width="${extent.width}" height="${extent.height}"`);
	});
}

// Collects every distinct (font, size) pair actually used in the document
// and builds the matching style map + CSS rules for them.
function buildRunStyling(document) {
	const runs = mammoth.transforms.getDescendantsOfType(document, 'run');
	const seen = new Map();
	runs.forEach((run) => {
		const styleName = runStyleName(run);
		if (styleName && !seen.has(styleName)) {
			seen.set(styleName, run);
		}
	});

	const styleMap = [];
	const cssRules = [];
	seen.forEach((run, styleName) => {
		const className = `docx-run-${fontSlug(styleName)}`;
		styleMap.push(`r[style-name='${styleName}'] => span.${className}`);
		const declarations = [];
		if (run.font) {
			const normalized = run.font.toLowerCase().trim();
			const family = KNOWN_KHMER_FONTS[normalized] || `'${run.font}'`;
			declarations.push(`font-family: ${family}, 'Calibri', 'Carlito', sans-serif`);
		}
		if (run.fontSize) {
			declarations.push(`font-size: ${run.fontSize}pt`);
		}
		cssRules.push(`.docx-page .${className} { ${declarations.join('; ')}; }`);
	});

	return { styleMap, css: cssRules.join('\n') };
}

function NavBar({ children }) {
	return (
		<Flex align="center" justify="center" px={3} bg="white" flexShrink={0} h="44px" borderBottomWidth="1px" borderColor="gray.200" gap={1} wrap="nowrap">
			{children}
		</Flex>
	);
}

const TB = React.forwardRef((props, ref) => (
	<IconButton ref={ref} variant="ghost" size="sm" borderRadius="full" sx={{ _disabled: { opacity: 1, cursor: 'default' } }} {...props} />
));

// Collapses any run of whitespace to a single space, returning both the
// collapsed string and a map from each of its character positions back to
// the matching offset in the original string. mammoth's extractRawText (the
// source of the AI's `context`/`original` snippets) inserts "\n\n" between
// paragraphs; the live DOM has no such separator between block elements at
// all — so the AI's snippets and the rendered text disagree on exactly how
// much whitespace sits at paragraph boundaries, even when the actual words
// are identical. Comparing both sides with whitespace collapsed makes the
// match resilient to that, while the position map lets the eventual match
// still be translated back to a real offset into the uncollapsed text.
function DocxViewer({ isOpen = true, onClose = () => null, url }) {
	const { t } = useTranslation();
	const toast = useToast();
	const { currentUser: user } = useStore((state) => state.currentUser);
	const roles = user?.role.split(',') || [];
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [html, setHtml] = useState('');
	const [runCss, setRunCss] = useState('');
	const [zoom, setZoom] = useState(1);
	const [rawText, setRawText] = useState('');
	// Each suggestion: { id, original, suggestion, context, status: 'pending'|'accepted'|'rejected' }
	const [spellingSuggestions, setSpellingSuggestions] = useState(null);
	// Document-level advisory notes (e.g. addressee/body company mismatch) —
	// read-only, no accept/reject action applies to these.
	const [documentNotes, setDocumentNotes] = useState([]);
	const [hoveredSuggestionId, setHoveredSuggestionId] = useState(null);
	const [spellPanelOpen, setSpellPanelOpen] = useState(false);
	const [spellCheckRequested, setSpellCheckRequested] = useState(false);
	const pageRef = useRef(null);
	const scrollContainerRef = useRef(null);

	const [checkSpelling, { loading: checkingSpelling }] = useMutation(CHECK_SPELLING, {
		onCompleted: (data) => {
			const result = data.checkSpelling || { suggestions: [], notes: [] };
			const withIds = (result.suggestions || []).map((s, i) => ({ ...s, id: `spell-${i}`, status: 'pending' }));
			setSpellingSuggestions(withIds);
			setDocumentNotes(result.notes || []);
			if (!withIds.length && !result.notes?.length) {
				toast({ status: 'success', title: t('No spelling issues found') });
			}
		},
		onError: (err) => {
			console.error('DocxViewer: checkSpelling failed', err);
			toast({ status: 'error', title: t('Spelling check failed'), description: t(err?.message) });
		},
	});

	// Marks each pending suggestion's matched text in the live DOM with a
	// <span data-spell-id="..."> once the results arrive and the container is
	// mounted. Runs off `spellingSuggestions` becoming non-null (fresh
	// results), not on every status change — accept/reject mutate the DOM
	// directly instead of re-running this whole pass, since remarking after
	// each action would try to re-locate text that no longer matches its
	// original `context` snippet once earlier accepts have rewritten it.
	useEffect(() => {
		if (!spellingSuggestions?.length || !pageRef.current) return;
		spellingSuggestions.forEach((s) => {
			if (s.status !== 'pending') return;
			if (pageRef.current.querySelector(`[data-spell-id="${s.id}"]`)) return;
			try {
				const range = locateSuggestionRange(pageRef.current, s.original, s.context);
				if (range) wrapRangeInSpan(range, s.id);
			} catch (err) {
				// A single suggestion whose offsets don't line up with the live DOM
				// (e.g. a Khmer text-normalization mismatch between the AI's context
				// snippet and the rendered text) shouldn't stop the rest from marking.
				console.warn('DocxViewer: failed to mark spelling suggestion', s.id, err);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [spellingSuggestions?.length, html]);

	// Toggles the highlight class on the DOM span matching the hovered panel
	// item — imperative because the span lives inside dangerouslySetInnerHTML
	// content that React doesn't track, so it can't be driven by a className
	// prop the normal way.
	useEffect(() => {
		if (!pageRef.current) return;
		const active = pageRef.current.querySelector(`.${SPELL_FLAG_ACTIVE_CLASS}`);
		if (active) active.classList.remove(SPELL_FLAG_ACTIVE_CLASS);
		if (!hoveredSuggestionId) return;
		const el = pageRef.current.querySelector(`[data-spell-id="${hoveredSuggestionId}"]`);
		if (el) {
			el.classList.add(SPELL_FLAG_ACTIVE_CLASS);
			el.scrollIntoView({ block: 'center', behavior: 'smooth' });
		}
	}, [hoveredSuggestionId]);

	const acceptSuggestion = useCallback((s) => {
		const el = pageRef.current?.querySelector(`[data-spell-id="${s.id}"]`);
		if (el) el.textContent = s.suggestion;
		setSpellingSuggestions((prev) => prev.map((p) => (p.id === s.id ? { ...p, status: 'accepted' } : p)));
		if (pageRef.current) setHtml(pageRef.current.innerHTML);
	}, []);

	const [rejectSpellingSuggestion] = useMutation(REJECT_SPELLING_SUGGESTION);

	const rejectSuggestion = useCallback(
		(s) => {
			const el = pageRef.current?.querySelector(`[data-spell-id="${s.id}"]`);
			if (el) {
				// Unwrap: drop the marker span but keep its (unmodified) text in place.
				const parent = el.parentNode;
				while (el.firstChild) parent.insertBefore(el.firstChild, el);
				parent.removeChild(el);
				parent.normalize();
			}
			setSpellingSuggestions((prev) => prev.map((p) => (p.id === s.id ? { ...p, status: 'rejected' } : p)));
			if (pageRef.current) setHtml(pageRef.current.innerHTML);
			// Fire-and-forget: builds up the shared rejection-count table so
			// repeatedly-dismissed corrections stop being suggested to anyone.
			rejectSpellingSuggestion({ variables: { original: s.original, suggestion: s.suggestion } }).catch((err) => {
				console.error('DocxViewer: failed to record spelling rejection', err);
				toast({ status: 'error', title: t('Failed to record spelling rejection'), description: t(err?.message) });
			});
		},
		[rejectSpellingSuggestion],
	);

	const pendingSuggestions = useMemo(() => spellingSuggestions?.filter((s) => s.status === 'pending') ?? [], [spellingSuggestions]);
	const acceptedSuggestions = useMemo(() => spellingSuggestions?.filter((s) => s.status === 'accepted') ?? [], [spellingSuggestions]);

	const [applyDocxCorrections, { loading: savingCorrections }] = useMutation(APPLY_DOCX_CORRECTIONS, {
		onCompleted: (data) => {
			const { base64 } = data.applyDocxCorrections;
			const originalName = url?.split('/')?.pop()?.split('?X-Amz-Algorithm')?.[0] || 'document.docx';
			const downloadName = originalName.replace(/\.docx$/i, '') + '-corrected.docx';
			triggerBlobDownload(base64ToBlob(base64, DOCX_MIME_TYPE), downloadName);
		},
		onError: (err) => {
			console.error('DocxViewer: failed to save corrections', err);
			toast({ status: 'error', title: t('Failed to save corrections'), description: t(err?.message) });
		},
	});

	// Download button behavior: if the user hasn't accepted any spelling
	// corrections, this is just the original file — download it directly, no
	// server round trip. If they have, the on-screen HTML preview is *not*
	// what should be downloaded (it's the lossy approximation, not a real
	// .docx) — so this calls the server to redo the same edits directly
	// inside the original .docx's XML and downloads that result instead.
	const handleDownload = useCallback(() => {
		if (!url) return;
		if (!acceptedSuggestions.length) {
			const link = document.createElement('a');
			link.href = url;
			link.download = '';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			return;
		}
		applyDocxCorrections({
			variables: {
				fileUrl: url,
				corrections: acceptedSuggestions.map(({ original, suggestion, context }) => ({ original, suggestion, context })),
			},
		});
	}, [acceptedSuggestions, url, applyDocxCorrections]);

	useEffect(() => {
		if (!isOpen || !url) return;
		let cancelled = false;
		setLoading(true);
		setError(false);
		setHtml('');
		setRunCss('');
		setSpellingSuggestions(null);
		setDocumentNotes([]);
		setSpellPanelOpen(false);
		setSpellCheckRequested(false);

		fetch(url)
			.then((r) => {
				if (!r.ok) throw new Error(`HTTP ${r.status}`);
				return r.arrayBuffer();
			})
			.then(async (buf) => {
				// mammoth parses the style map once up front, before transformDocument
				// ever runs — so a style map generated *inside* transformDocument (which
				// is what buildRunStyling needs, since it depends on the actual fonts/
				// sizes in this specific document) can't reach that same conversion. We
				// do a first pass whose only job is to walk the parsed document and
				// collect the real (font, size) pairs, then a second pass with the full
				// style map — including the rules buildRunStyling just generated — to
				// produce the HTML we actually render.
				const csProps = await readComplexScriptRunProps(buf).catch((err) => {
					console.error('DocxViewer: failed to read w:cs/w:szCs from document.xml', err);
					return [];
				});
				const imageExtents = await readImageExtents(buf).catch((err) => {
					console.error('DocxViewer: failed to read wp:extent from document.xml', err);
					return [];
				});

				const elementTransform = mammoth.transforms._elements((element) => {
					if (element.type === 'paragraph') return transformParagraph(element);
					if (element.type === 'run') return transformRun(element);
					return element;
				});

				let runStyling = { styleMap: [], css: '' };
				const captureRunStyling = (document) => {
					if (csProps.length) applyComplexScriptRunProps(document, csProps);
					runStyling = buildRunStyling(document);
					return document;
				};
				return mammoth
					.convertToHtml({ arrayBuffer: buf }, { transformDocument: captureRunStyling })
					.then(() =>
						Promise.all([
							mammoth.convertToHtml(
								{ arrayBuffer: buf },
								{
									styleMap: [...STYLE_MAP, ...ALIGN_STYLE_MAP, ...runStyling.styleMap],
									transformDocument: elementTransform,
								},
							),
							mammoth.extractRawText({ arrayBuffer: buf }),
						]),
					)
					.then(([result, rawTextResult]) => ({ result, runStyling, imageExtents, rawTextResult }));
			})
			.then(({ result, runStyling, imageExtents, rawTextResult }) => {
				if (cancelled) return;
				const html = applyImageExtents(result.value, imageExtents).replace(/\t/g, '<span class="docx-tab"></span>');
				setHtml(html);
				setRunCss(runStyling.css);
				setRawText(rawTextResult.value);
				setLoading(false);
			})
			.catch((err) => {
				console.error('DocxViewer load error', err);
				if (!cancelled) {
					setError(true);
					setLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [isOpen, url]);

	const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, parseFloat((z + ZOOM_STEP).toFixed(2))));
	const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, parseFloat((z - ZOOM_STEP).toFixed(2))));

	// Clicking the zoom percentage toggles between 100% and a zoom level that
	// fits the page's fixed width to the visible scroll area — the same
	// "fit width" affordance most PDF/doc viewers bind to clicking the
	// zoom-level label. Toggling back to 100% (rather than re-fitting again)
	// gives predictable behavior on repeated clicks.
	const handleZoomLabelClick = useCallback(() => {
		setZoom((z) => {
			if (z !== 1) return 1;
			const containerWidth = scrollContainerRef.current?.clientWidth;
			if (!containerWidth) return 1;
			const availableWidth = containerWidth - 48; // leave a little breathing room on each side
			const fitZoom = availableWidth / PAGE_W;
			return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, parseFloat(fitZoom.toFixed(2))));
		});
	}, []);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="outside">
			<ModalOverlay />
			<ModalContent borderRadius={0} overflow="hidden" h="100vh" m={0}>
				<ModalHeader p={0}>
					<NavBar>
						<TB
							aria-label="zoom-out"
							icon={<FontAwesomeIcon icon={faSearchMinus} />}
							onClick={zoomOut}
							isDisabled={loading || error || zoom <= ZOOM_MIN}
						/>
						<Text
							as="button"
							fontSize="sm"
							color="gray.600"
							minW="46px"
							textAlign="center"
							userSelect="none"
							cursor="pointer"
							_hover={{ color: 'gray.900' }}
							onClick={handleZoomLabelClick}
							title={t('Fit to width')}
						>
							{Math.round(zoom * 100)}%
						</Text>
						<TB
							aria-label="zoom-in"
							icon={<FontAwesomeIcon icon={faSearchPlus} />}
							onClick={zoomIn}
							isDisabled={loading || error || zoom >= ZOOM_MAX}
						/>
						<Box w="1px" alignSelf="stretch" my="10px" bg="gray.200" mx="2px" flexShrink={0} />
						<Button
							aria-label="download"
							leftIcon={<FontAwesomeIcon icon={faDownload} />}
							variant="ghost"
							size="sm"
							onClick={handleDownload}
							isDisabled={!url}
							isLoading={savingCorrections}
						>
							{t('Download')}
						</Button>
						<Box w="1px" alignSelf="stretch" my="10px" bg="gray.200" mx="2px" flexShrink={0} />
						{(roles?.includes('assistant') ||
							roles?.includes('admin') ||
							(roles?.includes('nprp') && isUserPermitted(['nprpAiSpellChecker'], [...(user?.nprp ?? [])]))) && (
							<Button
								aria-label="check-spelling"
								leftIcon={<FontAwesomeIcon icon={faSpellCheck} />}
								variant="ghost"
								size="sm"
								isLoading={checkingSpelling}
								isDisabled={loading || error || !rawText}
								onClick={() => {
									// The AI check only ever fires once per document load — every
									// click after that (whether results are still loading or
									// already in, and however many times it's clicked) just
									// toggles the panel's visibility instead of re-querying.
									if (!spellCheckRequested) {
										setSpellCheckRequested(true);
										setSpellPanelOpen(true);
										checkSpelling({ variables: { text: rawText } });
									} else {
										setSpellPanelOpen((open) => !open);
									}
								}}
							>
								{t('Check spelling')}
							</Button>
						)}
					</NavBar>
					<Flex align="center" justify="center" gap={2} px={3} py="4px" bg="yellow.50" borderBottomWidth="1px" borderColor="yellow.200">
						<FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: 12, color: '#b7791f' }} />
						<Text fontSize="xs" color="yellow.800">
							{t('This preview may not display exactly as it would in Microsoft Word')}
						</Text>
					</Flex>
				</ModalHeader>
				<ModalCloseButton zIndex={2} />
				<ModalBody p={0} bg="gray.100" h="calc(100vh - 72px)" overflow="hidden">
					<Flex h="full">
						<Box ref={scrollContainerRef} flex={1} overflow="auto" h="full">
							{loading && (
								<Flex direction="column" align="center" justify="center" h="full">
									<Box w="160px">
										<Lottie loop animationData={AnimationFile} />
									</Box>
									<Text fontSize="sm" color="gray.500">
										{t('Loading document...')}
									</Text>
								</Flex>
							)}

							{error && (
								<Flex direction="column" align="center" justify="center" h="full" gap={3}>
									<FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: 40, color: '#e67e22' }} />
									<Text color="gray.600">{t('Unable to preview this document')}</Text>
								</Flex>
							)}

							{!loading && !error && (
								<Flex justify="center" py={6}>
									<Box
										ref={pageRef}
										className="docx-page"
										bg="white"
										boxShadow="0 0 0 1px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.12)"
										style={{
											width: `${PAGE_W}px`,
											padding: `${PAGE_MARGIN}px`,
											transform: `scale(${zoom})`,
											transformOrigin: 'top center',
											marginBottom: zoom > 1 ? `${(zoom - 1) * PAGE_W}px` : 0,
										}}
										dangerouslySetInnerHTML={{ __html: html }}
									/>
								</Flex>
							)}
						</Box>

						{spellPanelOpen && (
							<Box w="320px" flexShrink={0} h="full" overflow="auto" bg="white" borderLeftWidth="1px" borderColor="gray.200">
								<Flex align="center" justify="space-between" px={4} py={3} borderBottomWidth="1px" borderColor="gray.200">
									<Text fontWeight="semibold">{t('Spelling suggestions')}</Text>
									<Flex align="center" gap={2}>
										{!checkingSpelling && (
											<Text fontSize="xs" color="gray.500">
												{pendingSuggestions.length}
											</Text>
										)}
										<IconButton
											aria-label="close-spelling-panel"
											icon={<FontAwesomeIcon icon={faTimes} />}
											size="xs"
											variant="ghost"
											onClick={() => setSpellPanelOpen(false)}
										/>
									</Flex>
								</Flex>

								{checkingSpelling && (
									<Flex direction="column" align="center" justify="center" py={10} gap={3}>
										<Box w="90px">
											<Lottie loop animationData={AnimationFile1} />
										</Box>
										<Text fontSize="sm" color="gray.500">
											{t('Checking spelling...')}
										</Text>
									</Flex>
								)}

								{!checkingSpelling && spellingSuggestions && pendingSuggestions.length === 0 && (
									<Text p={4} color="gray.500" fontSize="sm">
										{t('No pending suggestions')}
									</Text>
								)}
								{!checkingSpelling &&
									pendingSuggestions.map((s) => (
										<Box
											key={s.id}
											p={3}
											borderBottomWidth="1px"
											borderColor="gray.100"
											bg={hoveredSuggestionId === s.id ? 'yellow.50' : 'white'}
											onMouseEnter={() => setHoveredSuggestionId(s.id)}
											onMouseLeave={() => setHoveredSuggestionId(null)}
										>
											<Text fontSize="sm">
												<Text as="span" color="red.500" textDecoration="line-through">
													{s.original}
												</Text>
												{' → '}
												<Text as="span" color="green.600" fontWeight="medium">
													{s.suggestion}
												</Text>
											</Text>
											{s.context && (
												<Text fontSize="xs" color="gray.500" mt={1} noOfLines={2}>
													{s.context}
												</Text>
											)}
											<Flex gap={2} mt={2}>
												<IconButton
													aria-label="accept-suggestion"
													icon={<FontAwesomeIcon icon={faCheck} />}
													size="xs"
													colorScheme="green"
													onClick={() => acceptSuggestion(s)}
												/>
												<IconButton
													aria-label="reject-suggestion"
													icon={<FontAwesomeIcon icon={faTimes} />}
													size="xs"
													colorScheme="red"
													variant="outline"
													onClick={() => rejectSuggestion(s)}
													ml={1}
												/>
											</Flex>
										</Box>
									))}

								{!checkingSpelling && documentNotes.length > 0 && (
									<Box p={3} borderTopWidth="1px" borderColor="gray.200" bg="orange.50">
										<Flex align="center" gap={2} mb={2}>
											<FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: 13, color: '#c05621' }} />
											<Text fontSize="xs" fontWeight="semibold" color="orange.800">
												{t('Note: possible issues beyond spelling')}
											</Text>
										</Flex>
										{documentNotes.map((n, i) => (
											<Text key={i} fontSize="xs" color="orange.900" mb={i < documentNotes.length - 1 ? 2 : 0}>
												• {n.note}
											</Text>
										))}
									</Box>
								)}
							</Box>
						)}
					</Flex>
				</ModalBody>
			</ModalContent>
			<style>{DOCX_CSS}</style>
			<style>{runCss}</style>
			<style>{SPELL_FLAG_CSS}</style>
		</Modal>
	);
}

// Word-like typography: Calibri-equivalent stack for Latin text, Khmer OS
// Siemreap for Khmer script (matches how MS Word on Khmer-locale machines
// renders .docx files authored with Khmer OS fonts). Word computes line
// height from each font's own internal ascent/descent metrics, so its
// single-spacing (w:line 240, lineRule "auto") reads as roughly 1x *only*
// inside Word's own metric table for that font. The Khmer OS family's actual
// glyph bounding boxes (tall stacking vowels/subscripts) are much taller
// than their reported line-gap, so browsers rendering the same nominal
// "single spacing" at line-height 1 crowd or clip those marks against the
// line above. 1.4 was tuned against a real Khmer OS Siemreap document render
// to keep marks clear without visibly loosening the layout vs. Word's own
// output — this is a font-metric compensation, not a spacing preference.
const DOCX_CSS = `
.docx-page {
	font-family: 'Calibri', 'Carlito', 'KhmerOSSiemreap', 'Khmer OS Siemreap', 'Hanuman', Arial, sans-serif;
	font-size: 11pt;
	line-height: 1.4;
	color: #000;
	box-sizing: border-box;
	line-break: strict;
	word-break: keep-all;
	overflow-wrap: break-word;
}
.docx-page p {
	/* Khmer letters/memos built from direct formatting (no named styles)
	   routinely set w:spacing after="0" and rely on empty paragraphs as
	   manual spacers instead — but mammoth drops empty paragraphs from its
	   HTML output entirely rather than emitting empty <p> tags, so that
	   spacing information is lost before it ever reaches us. A flat margin
	   is an approximation of the visual gap those dropped paragraphs left,
	   tuned against real Khmer OS Siemreap/Muol Light documents at the
	   sizes they actually use (10-14pt) rather than assuming Word's 8pt
	   Latin default.
	*/
	margin: 0 0 12pt 0;
	text-align: left;
}
.docx-page p.docx-align-center { text-align: center; }
.docx-page p.docx-align-right { text-align: right; }
.docx-page p.docx-align-justify { text-align: justify; text-justify: inter-word; }
/* Word hanging indent: label sits at the margin, wrapped lines indent to
   line up under the first word after the label's tab stop. */
.docx-page p.docx-indent-hang {
	padding-left: 36pt;
	text-indent: -36pt;
}
.docx-page p.docx-indent-first {
	text-indent: 24pt;
}
.docx-tab {
	display: inline-block;
	width: 36pt;
}
.docx-page h1, .docx-page h2, .docx-page h3, .docx-page h4 {
	font-family: 'Calibri Light', 'Carlito', 'KhmerOSMuolLight', 'Khmer OS Muol Light', 'Hanuman', Arial, sans-serif;
	color: #2e74b5;
	margin: 12pt 0 4pt 0;
	line-height: 1.2;
}
.docx-page h1 { font-size: 20pt; }
.docx-page h2 { font-size: 16pt; }
.docx-page h3 { font-size: 14pt; color: #1f4e79; }
.docx-page h4 { font-size: 12pt; color: #1f4e79; font-style: italic; }
.docx-page h1.docx-title {
	font-size: 28pt;
	color: #000;
	font-family: 'Calibri Light', 'Carlito', Arial, sans-serif;
	margin-bottom: 0;
}
.docx-page p.docx-subtitle {
	font-size: 14pt;
	color: #595959;
	margin-top: 0;
}
.docx-page blockquote {
	margin: 8pt 0 8pt 24pt;
	padding-left: 12pt;
	border-left: 3px solid #ccc;
	color: #404040;
	font-style: italic;
}
.docx-page blockquote.docx-intense-quote {
	border-left-color: #2e74b5;
	color: #2e74b5;
}
.docx-page table {
	border-collapse: collapse;
	width: 100%;
	margin: 8pt 0;
	font-size: 11pt;
}
.docx-page table td, .docx-page table th {
	border: 1px solid #bfbfbf;
	padding: 4pt 8pt;
	vertical-align: top;
}
.docx-page ul, .docx-page ol {
	margin: 0 0 8pt 0;
	padding-left: 28pt;
}
.docx-page li {
	margin-bottom: 2pt;
}
.docx-page img {
	max-width: 100%;
	height: auto;
}
.docx-page a {
	color: #0563c1;
	text-decoration: underline;
}
.docx-page strong { font-weight: 700; }
.docx-page em { font-style: italic; }
.docx-page hr {
	border: none;
	border-top: 1px solid #bfbfbf;
	margin: 12pt 0;
}
`;



export default DocxViewer;
